import type { ControlScheme } from "../config";
import { playerSeat, type RunState } from "../core";
import { phrases } from "../i18n";

/**
 * The seats the app offers and when its start screen asks for one (Increment 5.18). Pure, so
 * it is unit-tested in Node; `seatChooser.ts` draws the cards and `main.ts` starts the run.
 */
export type SeatName = "king" | "soldier";

const SEAT_PARAM = "seat";

/** The seat the address names: `?seat=king` or `?seat=soldier`; anything else names none. */
export function seatOfQuery(search: string): SeatName | undefined {
  const named = new URLSearchParams(search).get(SEAT_PARAM);
  return named === "king" || named === "soldier" ? named : undefined;
}

const CONTROLS_PARAM = "controls";

/**
 * The King's controls the address names (Increment CC2): `?controls=cities` or
 * `?controls=classic`, in every build; anything else names none and the build's default plays.
 * A room match never reads it, so both pages agree.
 */
export function controlsOfQuery(search: string): ControlScheme | undefined {
  const named = new URLSearchParams(search).get(CONTROLS_PARAM);
  return named === "cities" || named === "classic" ? named : undefined;
}

const VALOR_PARAM = "valor";

/**
 * The soldier's valor the address names (V1 of the Soldier's growth): `?valor=on` plays it
 * whatever the seat ships with, `?valor=off` without it, in every build, so a desk run can play
 * both; anything else names none and the seat plays as it ships. Only the soldier's seat reads it.
 */
export function valorOfQuery(search: string): boolean | undefined {
  const named = new URLSearchParams(search).get(VALOR_PARAM);
  return named === "on" ? true : named === "off" ? false : undefined;
}

const LUCK_PARAM = "luck";

/**
 * FL2: `?luck=off` plays the fight without heavy and glancing blows, `?luck=on` with them, on
 * either seat, in every build, so a desk run can play both; anything else names none and the
 * fight plays as it ships (with luck since D-090). A room match never reads it.
 */
export function luckOfQuery(search: string): boolean | undefined {
  const named = new URLSearchParams(search).get(LUCK_PARAM);
  return named === "on" ? true : named === "off" ? false : undefined;
}

const RIVAL_PLANS_PARAM = "rivalPlans";

/**
 * FL1: `?rivalPlans=shown` draws the rival's roads and arrows as they were drawn before the
 * rival's plans were hidden (A27, quiet-arrows), in every build, so a desk run can play both;
 * anything else hides them.
 */
export function rivalPlansOfQuery(search: string): boolean {
  return new URLSearchParams(search).get(RIVAL_PLANS_PARAM) === "shown";
}

const MOTION_PARAM = "motion";

/**
 * UP2: `?motion=reduced` plays the units' motion reduced (a step glides flat, a strike stands
 * still, a hit is its flash, a death and a drop fade), in every build, whatever the system asks,
 * so a desk run can judge it; anything else follows the system's `prefers-reduced-motion`.
 */
export function reducedMotionOfQuery(search: string): boolean {
  return new URLSearchParams(search).get(MOTION_PARAM) === "reduced";
}

/** The seat the run on screen is played from: a restart keeps it. */
export function seatOfRun(state: RunState): SeatName {
  return playerSeat(state).role === "soldier" ? "soldier" : "king";
}

/**
 * The ways to play (K1): the duel (one King a kingdom, the soldier's run one Spear under a
 * scripted King), the company duel, a King and four soldier seats in each kingdom, every seat
 * but the human's played by its script (`docs/design/KINGDOM-SEATS-IDEA.md` section 6), or two
 * against two (K6), four kingdoms on two sides, the human's kingdom beside a scripted ally, or the
 * war (K7), two sides of ten kingdoms where a kingdom falls with its capital.
 */
export type ModeName = "duel" | "company" | "allies" | "war";

const MODE_NAMES: readonly ModeName[] = ["duel", "company", "allies", "war"];

const MODE_PARAM = "mode";

/** The mode the address names, in every build: `?mode=company`, `?mode=allies` (K6) or `?mode=war` (K7); anything else names none (the duel). */
export function modeOfQuery(search: string): ModeName | undefined {
  const named = new URLSearchParams(search).get(MODE_PARAM);
  return MODE_NAMES.find((mode) => mode !== "duel" && mode === named);
}

/** The mode of the run on screen: a restart keeps it. */
export function modeOfRun(state: RunState): ModeName {
  if (state.rules.sides) {
    return state.rules.capitalFall ? "war" : "allies";
  }
  return state.rules.soldier?.seats ? "company" : "duel";
}

/**
 * Whether the start screen asks at boot. A seat named in the address starts as it is named (a
 * bug report, the smoke test, a link to one way to play), and a saved run waits behind its own
 * prompt: the chooser follows its "New run".
 */
export function opensChooser(boot: {
  readonly named: SeatName | undefined;
  readonly resumable: boolean;
}): boolean {
  return boot.named === undefined && !boot.resumable;
}

export interface SeatCard {
  readonly seat: SeatName;
  readonly title: string;
  /** What the seat does, in one line. */
  readonly line: string;
  /**
   * What the account has earned (M2, C1): its banked coins (VC: a run starts with none in hand
   * and banks what it keeps) and its renown; absent on a seat that has none of it.
   */
  readonly bank?: number;
  readonly renown?: number;
}

/**
 * The start screen's cards, the King's first, in the language of the opening: the soldier's
 * names what the account has earned.
 */
export function seatCards(account: {
  readonly purse: number;
  readonly renown: number;
}): readonly SeatCard[] {
  const words = phrases().common.seats;
  const earned = account.purse > 0 || account.renown > 0;
  return [
    { seat: "king", title: words.king, line: words.kingLine },
    {
      seat: "soldier",
      title: words.soldier,
      line: words.soldierLine,
      ...(earned ? { bank: account.purse, renown: account.renown } : {}),
    },
  ];
}
